/**
 * The browser's half of issue #152 — a training session's CONTEXT.
 *
 * `session_context` (`casual` | `coaching` | `team`) is WHO the session is
 * with. `category` (Batting, Bowling, …) is WHAT SKILL was trained. They are
 * two independent axes and the whole ticket exists because they were about
 * to become one list: adding "Team training" to the category dropdown would
 * make "team batting training" — an ordinary thing to record — impossible to
 * say, and would quietly break the skill filter at the same time.
 *
 * Everything in this module is a pure function over a session or a form
 * state, so the rules that decide what the dialog asks for can be tested
 * without mounting a dialog. The component imports them; it does not restate
 * them. The API enforces the same rules independently
 * (`api/src/routes/trainingSessions.ts`) — a form is not an enforcement
 * boundary, and these functions exist to keep the player from being told
 * "no" by a toast when the UI could have told them "this one, please" first.
 */

/** The default, and what every session recorded before #152 means. */
export const DEFAULT_CONTEXT = "casual";

/**
 * The three contexts, in the order the form offers them: the default first,
 * then the two that ask for something extra.
 *
 * The labels say "1:1 coaching" and not "Coaching" because `coach_name`
 * already appears on team and casual sessions — a club coach can run a team
 * session — and "Coaching" alone would read as "was a coach there", which is
 * the question this field is NOT asking.
 */
export const SESSION_CONTEXTS = [
  { value: "casual", label: "Casual training", hint: "On your own, or with a mate." },
  { value: "coaching", label: "1:1 coaching", hint: "A session with a coach, for you." },
  { value: "team", label: "Team training", hint: "A squad or team session." },
];

export const SESSION_CONTEXT_VALUES = SESSION_CONTEXTS.map((c) => c.value);

/** Filter option shown above the list, on both axes. `all` is the default. */
export const ALL_CONTEXTS = "all";

export function contextLabel(value) {
  return SESSION_CONTEXTS.find((c) => c.value === value)?.label || SESSION_CONTEXTS[0].label;
}

/**
 * Which of the two "who with" fields this context asks for.
 *
 * `coachName: "required"` on `coaching` — a 1:1 session with nobody named is
 * not a record of anything, and the API 400s it.
 * `coachName: "hidden"` on `casual` — Martin, Discord 2026-08-06: "No need to
 * add who with." Considered and declined; do not add it back as an
 * obvious-looking gap.
 * `squadName` only on `team`, and it is free text: there is no club or team
 * entity (#103/#158 are parked), so this is a label, not a relation.
 */
export function contextFields(value) {
  switch (value) {
    case "coaching":
      return { coachName: "required", squadName: false };
    case "team":
      return { coachName: "optional", squadName: true };
    default:
      return { coachName: "hidden", squadName: false };
  }
}

/**
 * The fields a BOOKING owns once #146 exists — when, how long, with whom,
 * where. Same list as the API's `SCHEDULE_FIELDS`; both are keyed off the
 * ticket's own wording, and both change together when a real booking teaches
 * us what it actually owns.
 */
export const SCHEDULE_FIELDS = [
  "training_date",
  // #463. "When" is the date and the time on it. The API's SCHEDULE_FIELDS
  // gained the same entry in the same slice, for the same reason the
  // `ground_id` note below gives: locking half of a field pair leaves the
  // booked session free to drift by the half that stayed editable.
  "training_time",
  "duration_minutes",
  "coach_name",
  "location",
  // #356. "Where" stopped being one column: `location` is the label, and
  // `ground_id` is the record it names. Locking only the label would let a
  // booked session be repointed at a different ground while displaying the
  // booking's name. The API's `SCHEDULE_FIELDS` gained the same entry.
  "ground_id",
  // #423, caught by #166. Same argument one level down, and the API's list
  // already carries it: a booking that reserved net 3 is not satisfied by the
  // session claiming net 1. "Where" is the venue AND the surface, so the lock
  // covers all three keys. This copy was the half that had not caught up —
  // #423's slice added no frontend file, so the drift arrived with the
  // column.
  "facility_id",
];

/**
 * A session is schedule-locked when it CAME FROM A BOOKING, not when it is
 * labelled `coaching`. That distinction is the point: a player who logs their
 * own coaching session typed those details themselves and may fix them; a
 * session that arrived from a confirmed booking would desync from the booking
 * if its date moved here.
 *
 * Nothing sets `booking_id` yet (#146 has not shipped). The read-only state is
 * built now anyway, per the ticket: retrofitted later it becomes a second
 * form.
 */
export function isScheduleLocked(session) {
  return Boolean(session && session.booking_id);
}

/** The sentence shown where the locked fields are, so the greyed-out inputs are explained rather than just dead. */
export const SCHEDULE_LOCKED_NOTICE =
  "This session came from a booking. Its date, duration, coach and location are set by the booking — your reflection is still yours to write.";

/**
 * Client-side validation for the context rules, returning the same field-keyed
 * error map the rest of this form uses. Only the coaching/coach-name rule can
 * fail: the context itself comes from a fixed list of buttons, and a squad
 * name is optional even on a team session (a player may not know the squad's
 * formal name, and blocking the save over it would be inventing a
 * requirement).
 */
export function validateContext(form) {
  const errors = {};
  if (contextFields(form.session_context).coachName === "required" && !String(form.coach_name || "").trim()) {
    errors.coach_name = "Coach name is required for a 1:1 coaching session";
  }
  return errors;
}

/**
 * The context-dependent part of a create/update payload: the context itself
 * (falling back to the default rather than sending a value the API's enum
 * would 400 on) and a squad name only where one means something. The API
 * normalises the same way — this is so the form and the row agree about what
 * was saved, not so the API can trust the client.
 */
export function contextPayload(form) {
  const context = SESSION_CONTEXT_VALUES.includes(form.session_context) ? form.session_context : DEFAULT_CONTEXT;
  return {
    session_context: context,
    squad_name: contextFields(context).squadName ? String(form.squad_name || "").trim() || null : null,
  };
}

/**
 * Drop the four booking-owned keys from a finished payload.
 *
 * The API's lock compares VALUES rather than keys, so echoing them back
 * unchanged would in fact be accepted — this is not what makes the lock work.
 * It runs anyway because a form should not claim to be writing fields it
 * rendered read-only, and because a stale copy of a field the booking has
 * since moved is exactly the desync the lock exists to prevent.
 */
export function withoutLockedFields(payload, locked) {
  if (!locked) return payload;
  const out = { ...payload };
  for (const key of SCHEDULE_FIELDS) delete out[key];
  return out;
}
